<template>
  <span data-dusk="toggle-active-user">
    <a-popconfirm
      v-if="userIsActive"
      :ok-text="(hasResponsibilitiesForHandover ? 'Draag over en deactiveer' : 'Deactiveer')"
      cancel-text="Nee"
      @confirm="checkResponsibilitiesForHandover"
      placement="bottomRight"
    >
      <template v-slot:title>
        <strong>{{ fullName }} deactiveren?</strong><br>
        <span v-if="hasResponsibilitiesForHandover">
          <em>{{ fullName }}</em> heeft nog taken/dossiers aan zich gekoppeld.<br>
          In de volgende stap kunnen deze worden overgedragen.
        </span>
      </template>
      <a-button
        :title="`Deactiveer ${fullName}`"
        :loading="loadingUser || loadingToggleActive"
        type="danger"
        class="btn btn-primary btn-sm toggle-active-button"
      >
        <i class="mdi mdi-power" />
      </a-button>
    </a-popconfirm>

    <a-popconfirm
      v-else
      ok-text="Activeer"
      cancel-text="Nee"
      @confirm="activateUser"
      placement="bottomRight"
    >
      <template v-slot:title>
        <strong>{{ fullName }} activeren?</strong>
      </template>
      <a-button
        :title="`Activeer ${fullName}`"
        :loading="loadingUser || loadingToggleActive"
        type="secondary"
        class="btn btn-primary btn-sm toggle-active-button"
      >
        <i class="mdi mdi-power" />
      </a-button>
    </a-popconfirm>

    <a-drawer
        placement="right"
        :visible="drawerVisible"
        @close="closeDrawer"
        width="50%">

      <h3 style="padding: 1.5rem 0.5rem; margin-bottom: 0">
        <a-button type="danger" class="xl-button" shape="circle" size="large" style="cursor: default">
          <i class="mdi mdi-power" />
        </a-button>
        Deactiveer: {{ fullName }}
      </h3>

      <a-alert type="info" show-icon style="margin-bottom: 1.5em;">
        <template v-slot:message>
          <strong>{{ fullName }}</strong> heeft nog taken/dossiers aan zich gekoppeld. <br>
          Deze kunnen hier <strong>éénmalig</strong> worden overgedragen.
        </template>
      </a-alert>

      <a-card>
        <template v-slot:title>
          {{ countTasks > 1 ? 'Taken' : 'Taak' }}
          <a-badge :count="countTasks" :number-style="{ backgroundColor: badgeColor }"></a-badge>
        </template>

        <a-row :gutter="gutter" v-if="countTasks">
          <a-col :span="span.label">
            <a-popover title="Wijs toe aan gebruiker(s) en/of afdeling(en)">
              <template v-slot:content>
                Huidige actieve taken gekoppeld aan {{ fullName }} overdragen aan andere gebruiker(s) en/of afdeling(en).
              </template>
              Wijs toe aan gebruiker(s)
              <span style="margin-left: 0.5em;"><information-outline-icon title="info" /></span>
            </a-popover>
          </a-col>
          <a-col :span="span.input">
            <users-select
                field-name="tasks"
                v-model="tasksToUsers"
                :custom-options="selectableUsers"
                :disabled="loadingToggleActive"
            ></users-select>
          </a-col>
        </a-row>
        <a-row :gutter="gutter" v-if="countTasks">
          <a-col :span="span.label">
            <a-popover title="Wijs toe aan gebruiker(s) en/of afdeling(en)">
              <template v-slot:content>
                Huidige actieve taken gekoppeld aan {{ fullName }} overdragen aan andere gebruiker(s) en/of afdeling(en).
              </template>
              Wijs toe aan afdeling(en)
              <span style="margin-left: 0.5em;"><information-outline-icon title="info" /></span>
            </a-popover>
          </a-col>
          <a-col :span="span.input">
            <user-departments-select
                v-model="tasksToDepartments"
                :disabled="loadingToggleActive"
            ></user-departments-select>
          </a-col>
        </a-row>

        <p v-else>
          Er zijn geen actieve taken over te dragen.
        </p>
      </a-card>

      <br>

      <a-card>
        <template v-slot:title>
          {{ titleDossiersPrivate(countDossiersPrivate) }}
          <a-badge :count="countDossiersPrivate" :number-style="{ backgroundColor: badgeColor }"></a-badge>
        </template>

        <a-row :gutter="gutter" v-if="countDossiersPrivateAdvisor">
          <a-col :span="span.label">
            <a-popover title="Selecteer een nieuwe adviseur">
              <template v-slot:content>
                {{ getDescription(countDossiersPrivateAdvisor, 'private', 'adviseur') }}
              </template>
              Selecteer een nieuwe <em>adviseur</em>
              <span style="margin-left: 0.5em;"><information-outline-icon title="info" /></span>
            </a-popover>
          </a-col>
          <a-col :span="span.input">
            <users-select
                field-name="private_advisor"
                v-model="privateAdvisorToUser"
                :multiple="false"
                :allow-clear="true"
                :custom-options="selectableUsers"
                :disabled="loadingToggleActive"
            ></users-select>
          </a-col>
        </a-row>
        <a-row :gutter="gutter" v-if="countDossiersPrivateAccountManager">
          <a-col :span="span.label">
            <a-popover title="Selecteer een nieuwe behandelaar">
              <template v-slot:content>
                {{ getDescription(countDossiersPrivateAccountManager, 'private', 'behandelaar') }}
              </template>
              Selecteer een nieuwe <em>behandelaar</em>
              <span style="margin-left: 0.5em;"><information-outline-icon title="info" /></span>
            </a-popover>
          </a-col>
          <a-col :span="span.input">
            <users-select
                fieldName="private_account_manager"
                v-model="privateAccountManagerToUser"
                :multiple="false"
                :allow-clear="true"
                :custom-options="selectableUsers"
                :disabled="loadingToggleActive"
            ></users-select>
          </a-col>
        </a-row>

        <p v-if="!countDossiersPrivateAdvisor && !countDossiersPrivateAccountManager">
          Geen Particuliere Dossiers over te dragen.
        </p>
      </a-card>

      <br>

      <a-card>
        <template v-slot:title>
          {{ titleDossiersBusiness(countDossiersBusiness) }}
          <a-badge :count="countDossiersBusiness" :number-style="{ backgroundColor: badgeColor }"></a-badge>
        </template>

        <a-row :gutter="gutter" v-if="countDossiersBusinessAdvisor">
          <a-col :span="span.label">
            <a-popover title="Selecteer een nieuwe adviseur">
              <template v-slot:content>
                {{ getDescription(countDossiersBusinessAdvisor, 'business', 'adviseur') }}
              </template>
              Selecteer een nieuwe <em>adviseur</em>
              <span style="margin-left: 0.5em;"><information-outline-icon title="info" /></span>
            </a-popover>
          </a-col>
          <a-col :span="span.input">
            <users-select
                field-name="business_advisor"
                v-model="businessAdvisorToUser"
                :multiple="false"
                :allow-clear="true"
                :custom-options="selectableUsers"
                :disabled="loadingToggleActive"
            ></users-select>
          </a-col>
        </a-row>
        <a-row :gutter="gutter" v-if="countDossiersBusinessAccountManager">
          <a-col :span="span.label">
            <a-popover title="Selecteer een nieuwe behandelaar">
              <template v-slot:content>
                {{ getDescription(countDossiersBusinessAccountManager, 'business', 'behandelaar') }}
              </template>
              Selecteer een nieuwe <em>behandelaar</em>
              <span style="margin-left: 0.5em;"><information-outline-icon title="info" /></span>
            </a-popover>
          </a-col>
          <a-col :span="span.input">
            <users-select
                field-name="business_account_manager"
                v-model="businessAccountManagerToUser"
                :multiple="false"
                :allow-clear="true"
                :custom-options="selectableUsers"
                :disabled="loadingToggleActive"
            ></users-select>
          </a-col>
        </a-row>

        <p v-if="!countDossiersBusinessAdvisor && !countDossiersBusinessAccountManager">
          Geen Zakelijke Dossiers over te dragen.
        </p>
      </a-card>

      <div class="flex-row justify-content-start gap-md" style="margin-top: 1.5em;">
        <dd-button key="back" @click="closeDrawer" outline type="primary" style="color:grey; border-color: grey;">
          Annuleer
        </dd-button>

        <a-popconfirm
            v-if="missingHandoverSelection.length"
            ok-text="Ja, opslaan"
            cancel-text="Annuleer"
            @confirm="deactivateUser"
            placement="topRight"
        >
          <template v-slot:title>
            Voor onderstaande {{ missingHandoverSelection.length > 1 ? 'zijn' : 'is' }} nog geen
            andere gebruiker{{ missingHandoverSelection.length > 1 ? 's' : '' }} geselecteerd:
            <ul>
              <li v-for="(item, index) in missingHandoverSelection" :key="index">{{ item }}</li>
            </ul>
            <strong>Doorgaan zonder bovenstaande over te dragen aan andere gebruiker?</strong>
          </template>
          <dd-button
              key="submit"
              :type="anyResponsibilitySelectedForHandover ? 'primary' : 'danger'"
              outline
              :loading="loadingToggleActive"
          >
            Opslaan {{ anyResponsibilitySelectedForHandover ? 'met beperkte' : 'zonder' }} overdracht
          </dd-button>
        </a-popconfirm>

        <dd-button v-else key="submit" type="primary" :loading="loadingToggleActive" @click="deactivateUser">
          Opslaan
        </dd-button>

      </div>
    </a-drawer>
  </span>
</template>

<script>
import store from "../store";
import {mapGetters} from "vuex";
import userApi from "~/api/user";

export default {
  name: "ToggleUserActiveComponent",
  components: {
  },
  props: {
    userId: {
      type: [String, Number],
      required: true,
    },
  },
  data() {
    return {
      user: null,
      drawerVisible: false,
      loadingUser: false,
      loadingToggleActive: false,
      tasksToUsers: [],
      tasksToDepartments: [],
      privateAdvisorToUser: undefined,
      privateAccountManagerToUser: undefined,
      businessAdvisorToUser: undefined,
      businessAccountManagerToUser: undefined,
      countTasks: 0,
      countDossiersPrivateAdvisor: 0,
      countDossiersPrivateAccountManager: 0,
      countDossiersBusinessAdvisor: 0,
      countDossiersBusinessAccountManager: 0,
      badgeColor: '#1890ff',
      span: {
        label: 10,
        input: 14,
      },
      gutter: [16, 8],
      includes: [
        'all_active_task_count',
        'private_advisor_count',
        'private_account_manager_count',
        'business_advisor_count',
        'business_account_manager_count',
      ],
    }
  },
  created() {
    this.loadingUser = true;

    userApi.getUserById(this.userId, this.includes)
        .then((response) => {
          this.user = response.data;

          this.countTasks                          = response.data?.all_active_task_count?.data.count || 0;
          this.countDossiersPrivateAdvisor         = response.data?.private_advisor_count?.data.count || 0;
          this.countDossiersPrivateAccountManager  = response.data?.private_account_manager_count?.data.count || 0;
          this.countDossiersBusinessAdvisor        = response.data?.business_advisor_count?.data.count || 0;
          this.countDossiersBusinessAccountManager = response.data?.business_account_manager_count?.data.count || 0;
        })
        .catch((error) => {
          this.$notification["error"]({
            message: "Fout",
            description: "Gebruiker niet gevonden.",
          });
          console.error(error);
        })
        .finally(() => {
          this.loadingUser = false;
        });

    const STORE_KEY = '$_user'
    // eslint-disable-next-line no-underscore-dangle
    if (!(STORE_KEY in this.$store._modules.root._children)) {
      this.$store.registerModule(STORE_KEY, store)
    }
  },
  methods: {
    closeDrawer() {
      this.drawerVisible = false;
    },
    checkResponsibilitiesForHandover() {
      if (this.hasResponsibilitiesForHandover) {
        this.drawerVisible = true;
      } else {
        this.deactivateUser();
      }
    },
    deactivateUser() {
      this.loadingToggleActive = true;
      let forceDeactivation = true;

      userApi.deactivateUser(this.userId, forceDeactivation, this.responsibilitiesHandoverTo)
          .then(() => {
            this.$emit('user-deactivated', this.userId); // for future use

            this.$notification["success"]({
              message: "Gedeactiveerd",
              description: `${this.fullName} is gedeactiveerd.`,
            });

            // Allow user to read success message before reloading page
            this.reloadPageAfterTimeOut();
          })
          .catch((error) => {
            this.$notification["error"]({
              message: "Er ging iets mis",
              description: `${this.fullName} deactiveren niet gelukt.`,
            });
            console.error(error);
          })
          .finally(() => {
            this.drawerVisible = false;
            this.loadingToggleActive = false;
          });
    },
    activateUser() {
      this.loadingToggleActive = true;

      userApi.activateUser(this.userId)
          .then(() => {
            this.$emit('user-activated', this.userId); // for future use

            this.$notification["success"]({
              message: "Geactiveerd",
              description: `${this.fullName} is geactiveerd.`,
            });

            // allow user to read success message before reloading page
            this.reloadPageAfterTimeOut();
          })
          .catch((error) => {
            this.$notification["error"]({
              message: "Er ging iets mis",
              description: `${this.fullName} activeren niet gelukt.`,
            });
            console.error(error);
          })
          .finally(() => {
            this.loadingToggleActive = false;
          });
    },
    reloadPageAfterTimeOut() {
      setTimeout(() => {
        location.reload();
      }, 1200);
    },
    titleDossiersPrivate(count) {
      return count > 1 ? 'Particuliere Dossiers' : 'Particulier Dossier';
    },
    titleDossiersBusiness(count) {
      return count > 1 ? 'Zakelijke Dossiers' : 'Zakelijk Dossier';
    },
    getDescription(count, dossierType, role) {
      let dossiersDescription = 'dossiers';

      switch (dossierType) {
        case 'private':
          dossiersDescription = this.titleDossiersPrivate(count).toLowerCase();
          break;
        case 'business':
          dossiersDescription = this.titleDossiersBusiness(count).toLowerCase();
          break;
      }

      return `Huidige ${dossiersDescription} waar ${this.fullName} ${role} is, overdragen aan andere ${role}.`;
    },
  },
  computed: {
    ...mapGetters('$_user', [
      'users',
    ]),
    userIsActive() {
      return this.user?.active;
    },
    fullName() {
      return this.user?.fullname || 'Gebruiker';
    },
    selectableUsers() {
      let userId = (isNaN(this.userId) ? this.userId : parseInt(this.userId));
      return this.users.filter(user => user.id !== userId);
    },
    countDossiersPrivate() {
      return this.countDossiersPrivateAdvisor + this.countDossiersPrivateAccountManager;
    },
    countDossiersBusiness() {
      return this.countDossiersBusinessAdvisor + this.countDossiersBusinessAccountManager;
    },
    countResponsibilities() {
      return this.countTasks + this.countDossiersPrivate + this.countDossiersBusiness;
    },
    hasResponsibilitiesForHandover() {
      return this.countResponsibilities > 0;
    },
    missingHandoverSelection() {
      let missingHandoverItems = [];

      if (this.countTasks && (!this.tasksToUsers.length && !this.tasksToDepartments.length)) {
        missingHandoverItems.push('Taken');
      }

      if (this.countDossiersPrivateAdvisor && typeof this.privateAdvisorToUser === 'undefined') {
        missingHandoverItems.push('Particulier adviseur');
      }

      if (this.countDossiersPrivateAccountManager && typeof this.privateAccountManagerToUser === 'undefined') {
        missingHandoverItems.push('Particulier behandelaar');
      }

      if (this.countDossiersBusinessAdvisor && typeof this.businessAdvisorToUser === 'undefined') {
        missingHandoverItems.push('Zakelijk adviseur');
      }

      if (this.countDossiersBusinessAccountManager && typeof this.businessAccountManagerToUser === 'undefined') {
        missingHandoverItems.push('Zakelijk behandelaar');
      }

      return missingHandoverItems;
    },
    anyResponsibilitySelectedForHandover() {
      return this.missingHandoverSelection.length < this.sumResponsibilitiesCategories;
    },
    sumResponsibilitiesCategories() {
      return (this.countTasks > 0 ? 1 : 0)
          + (this.countDossiersPrivateAdvisor > 0 ? 1 : 0)
          + (this.countDossiersPrivateAccountManager > 0 ? 1 : 0)
          + (this.countDossiersBusinessAdvisor > 0 ? 1 : 0)
          + (this.countDossiersBusinessAccountManager > 0 ? 1 : 0);
    },
    responsibilitiesHandoverTo() {
      let handoverItems = {};

      if (this.countTasks) {
        handoverItems.tasks = {};

        if (this.tasksToUsers.length) {
          handoverItems.tasks.to_users = this.tasksToUsers;
        }

        if (this.tasksToDepartments.length) {
          handoverItems.tasks.to_departments = this.tasksToDepartments;
        }
      }

      if (this.countDossiersPrivateAdvisor && typeof this.privateAdvisorToUser !== 'undefined') {
        handoverItems.private_advisor_to_user = this.privateAdvisorToUser;
      }

      if (this.countDossiersPrivateAccountManager && typeof this.privateAccountManagerToUser !== 'undefined') {
        handoverItems.private_account_manager_to_user = this.privateAccountManagerToUser;
      }

      if (this.countDossiersBusinessAdvisor && typeof this.businessAdvisorToUser !== 'undefined') {
        handoverItems.business_advisor_to_user = this.businessAdvisorToUser;
      }

      if (this.countDossiersBusinessAccountManager && typeof this.businessAccountManagerToUser !== 'undefined') {
        handoverItems.business_account_manager_to_user = this.businessAccountManagerToUser;
      }

      return handoverItems;
    },
  }
}
</script>

<style scoped>
.toggle-active-button {
  padding: 5px 10px;
  height: auto !important;
}

</style>